import Header from "../header/header";
import Footer from "../footer/footer";
import NavBar from "../nav/nav-bar";
import ReportNav from "./report-nav";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { faCommentsDollar } from "@fortawesome/free-solid-svg-icons";
import { useEffect, useState } from "react";
import Service from "./../webservice/http";
import { useHistory } from "react-router-dom";
import { storage } from "../utils/storage";
import { DateRangePicker } from "react-date-range";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import ReportFilter from "./report-filter";
import { FaFilter } from "react-icons/fa";
import ReportFilterBanner from "./report-filter-banner";

const ProductIntentReport = () => {
  const history = useHistory();
  const services = new Service();
  const role = storage.getItem("role");
  const isAgentRole =
    role === "Agent" ||
    role === "Agenth" ||
    role === "Agent Collections" ||
    role.includes("Agent");

  const [showRealData, setShowRealData] = useState(true);

  const [customerData, setCustomerData] = useState("");

  const [dateDifference, setDateDifference] = useState("");

  const [displayDateCategory, setDisplayDateCategory] = useState(null);

  /**
   * Fetch Customer intent report data
   */

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  useEffect(() => {
    const today = new Date();
    const endDate = new Date(today);
    endDate.setDate(today.getDate() - dateDifference);

    const formatDate = (date) => {
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    };

    if (dateDifference) {
      setEndDate(formatDate(endDate));
      setStartDate(formatDate(today));
    } else {
      setEndDate("");
      setStartDate("");
    }
  }, [dateDifference]);

  // Helper: format a Date as dd/mm/yyyy
  const formatDMY = (date) => {
    const d = String(date.getDate()).padStart(2, "0");
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const y = date.getFullYear();
    return `${d}/${m}/${y}`;
  };

  // Fetch collection-intent bar chart data
  async function fetchCollectionIntent(fromDate, toDate) {
    let url = "api/report/collection-intent/";
    if (fromDate && toDate) {
      url += `?from_date=${fromDate}&to_date=${toDate}`;
    }
    const res = await services.get(url);
    if (!res || res === "TypeError: Failed to fetch") {
      console.error("Collection intent API: connection error");
      return;
    }
    try {
      if (res.code === "token_not_valid") {
        storage.clear();
        history.push("/login");
        return;
      }
      const items = res.data || [];
      const categories = items.map((item) => item.product_name);
      const makeData = (key) =>
        items.map((item) => item.intents?.[key]?.count ?? 0);
      setProductWiseCategories(categories);
      setProductWiseSeries([
        { name: "Full Settlement", data: makeData("complete_settlement") },
        { name: "Partial Settlement", data: makeData("partial_settlement") },
        { name: "Broken Promise", data: makeData("promise_broken") },
        { name: "Denial", data: makeData("denial") },
        { name: "Legal Action", data: makeData("legal_action_risk") },
      ]);
    } catch (e) {
      console.error("Collection intent parse error:", e);
    }
  }

  const [customerIntentData, setCustomerIntentData] = useState([]);

  const [callDistributionData, setCallDistributionData] = useState([]);

  const [aggregatedCustomerIntentData, setAggregatedCustomerIntentData] =
    useState([]);

  // Chart state — populated from API
  const [productWiseCategories, setProductWiseCategories] = useState([]);
  const [productWiseSeries, setProductWiseSeries] = useState([]);

  // On mount: load last 7 days by default
  useEffect(() => {
    const today = new Date();
    const from = new Date(today);
    from.setDate(today.getDate() - 7);
    fetchCollectionIntent(formatDMY(from), formatDMY(today));
    if (!isAgentRole) fetchHierarchyProductIntent();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Report filter state (non-agent roles) ──
  const [reportFilterWidth, setReportFilterWidth] = useState(0);
  const [isReportFilterOpen, setIsReportFilterOpen] = useState(false);
  const [reportFilters, setReportFilters] = useState({});

  const changeReportFilterWidth = () => {
    if (reportFilterWidth === 550) {
      setReportFilterWidth(0);
      setIsReportFilterOpen(false);
    } else {
      setReportFilterWidth(550);
      setIsReportFilterOpen(true);
    }
  };
  const handleReportFilterApply = (f) => setReportFilters(f);
  const handleReportFilterReset = () => setReportFilters({});

  const buildQueryParams = (filters) => {
    const params = new URLSearchParams();
    const mapping = {
      lob: "lob_name",
      manager: "manager_name",
      team_lead: "team_lead_name",
      agent: "agent_name",
      dca_organization: "dca_organization_name",
      dca_manager: "dca_manager_name",
      dca_team: "dca_team_lead_name",
      dca_agent: "dca_agent_name",
      collection_strategy: "collection_strategy",
      product_type: "product_name",
      time_period: "period",
      from_date: "from_date",
      to_date: "to_date",
      bucket_type: "bucket_type",
    };
    for (const [key, value] of Object.entries(filters)) {
      if (mapping[key] && value) params.append(mapping[key], value);
      else if (value && !mapping[key]) params.append(key, value);
    }
    return params.toString().replace(/\+/g, "%20");
  };

  // ── Hierarchy product intent state (non-agent roles) ──
  const [hierCategories, setHierCategories] = useState([]);
  const [hierSeries, setHierSeries] = useState([]);
  const [hierCalls, setHierCalls] = useState([]);

  const fetchHierarchyProductIntent = async (filters = {}) => {
    const qs = buildQueryParams(filters);
    const url = `api/call_verification/hierarchy-product-intent/${
      qs ? `?${qs}` : ""
    }`;
    const res = await services.get(url);
    if (!res || res === "TypeError: Failed to fetch") return;
    if (res.code === "token_not_valid") {
      storage.clear();
      history.push("/login");
      return;
    }
    const items = res.data || [];
    setHierCategories(items.map((d) => d.product_name));
    const makeData = (key) => items.map((d) => d.intents?.[key]?.count ?? 0);
    setHierSeries([
      {
        name: "Product Wise Calls",
        data: items.map((d) => d.total_calls ?? 0),
      },
      { name: "Settlement Intent", data: makeData("complete_settlement") },
      { name: "Partial Settlement", data: makeData("partial_settlement") },
      { name: "Broken Promise", data: makeData("promise_broken") },
      { name: "Legal Action", data: makeData("legal_action_risk") },
    ]);
    setHierCalls(res.detailed_calls || []);
  };

  // Re-fetch hierarchy data when filters change (Apply/Reset)
  useEffect(() => {
    if (!isAgentRole) fetchHierarchyProductIntent(reportFilters);
  }, [reportFilters]); // eslint-disable-line react-hooks/exhaustive-deps

  const productWiseIntentOptions = {
    chart: {
      type: "column",
      height: "300px",
    },
    title: {
      text: "",
    },
    credits: {
      enabled: false,
    },
    xAxis: {
      categories: productWiseCategories,
      gridLineWidth: 0,
    },
    yAxis: {
      min: 0,
      gridLineColor: "transparent",
      title: {
        text: "Number of Calls",
      },
    },
    plotOptions: {
      column: {
        stacking: "normal",
        borderRadius: 3,
        groupPadding: 0.05,
        pointPadding: 0,
        dataLabels: {
          enabled: false,
        },
      },
    },
    legend: {
      enabled: true,
      align: "center",
      verticalAlign: "bottom",
      layout: "horizontal",
    },
    series: productWiseSeries,
  };

  const [managerCommentData, setManagerCommentData] = useState({
    count: 0,
    change: 0,
  });

  const [opportunityData, setOpportunityData] = useState({
    count: 0,
    change: 0,
  });

  const [serviceRequestData, setServiceRequestData] = useState({
    count: 0,
    change: 0,
  });

  const [activeFilter, setActiveFilter] = useState("7");

  // --- Detailed calls table ---
  const [callsData, setCallsData] = useState([]);
  const [callsOutcomes, setCallsOutcomes] = useState([]);
  const [callsProducts, setCallsProducts] = useState([]);
  const [callSearchText, setCallSearchText] = useState("");
  const [callProductFilter, setCallProductFilter] = useState("");
  const [callOutcomeFilter, setCallOutcomeFilter] = useState("");
  const [dateRangeOpen, setDateRangeOpen] = useState(false);
  const [callSelectionRange, setCallSelectionRange] = useState(null);

  const fetchDetailedCalls = async ({
    search,
    product,
    outcome,
    fromDate,
    toDate,
  } = {}) => {
    let url = "api/report/collection-info-detailed/";
    const parts = [];
    if (fromDate && toDate) {
      parts.push(`from_date=${fromDate}`);
      parts.push(`to_date=${toDate}`);
    }
    if (search) parts.push(`search=${encodeURIComponent(search)}`);
    if (product) parts.push(`product=${encodeURIComponent(product)}`);
    if (outcome) parts.push(`outcome=${encodeURIComponent(outcome)}`);
    if (parts.length) url += `?${parts.join("&")}`;

    const res = await services.get(url);
    if (!res || res === "TypeError: Failed to fetch") {
      console.error("collection-info-detailed: connection error");
      return;
    }
    if (res.code === "token_not_valid") {
      storage.clear();
      history.push("/login");
      return;
    }
    setCallsData(res.data || []);
    setCallsOutcomes(res.filters?.outcomes || []);
    setCallsProducts(res.filters?.products || []);
  };

  useEffect(() => {
    const today = new Date();
    const from = new Date(today);
    from.setDate(today.getDate() - 7);
    fetchDetailedCalls({ fromDate: formatDMY(from), toDate: formatDMY(today) });
  }, []);

  useEffect(() => {
    const today = new Date();
    let fromDate, toDate;
    if (callSelectionRange) {
      fromDate = formatDMY(callSelectionRange.startDate);
      toDate = formatDMY(callSelectionRange.endDate);
    } else {
      const from = new Date(today);
      from.setDate(today.getDate() - 7);
      fromDate = formatDMY(from);
      toDate = formatDMY(today);
    }
    fetchDetailedCalls({
      search: callSearchText,
      product: callProductFilter,
      outcome: callOutcomeFilter,
      fromDate,
      toDate,
    });
  }, [
    callSearchText,
    callProductFilter,
    callOutcomeFilter,
    callSelectionRange,
  ]);

  const handleCallDateSelect = (ranges) => {
    setCallSelectionRange(ranges.selection);
  };
  /**
   * Customer intent chart config
   */
  const callDistributionOptions = {
    chart: {
      type: "pie",
      height: "250px",
      marginBottom: 70,
    },
    title: {
      text: "",
    },
    credits: {
      enabled: false,
    },
    plotOptions: {
      pie: {
        allowPointSelect: true,
        cursor: "pointer",
        dataLabels: {
          enabled: true,
          format: "{point.percentage:.1f} %",
          distance: -20,
        },
        showInLegend: true,
        size: "120%",
      },
    },
    legend: {
      align: "center",
      verticalAlign: "bottom",
      layout: "horizontal",
      y: 0,
      padding: 10,
    },
    series: [
      {
        name: "Delivered amount",
        data: callDistributionData,
      },
    ],
  };

  const customerIntentOptions = {
    chart: {
      type: "pie",
      height: "250px",
      marginBottom: 70,
    },
    title: {
      text: "Intent",
      align: "center",
      verticalAlign: "middle",
      y: -9,
    },
    credits: {
      enabled: false,
    },
    plotOptions: {
      pie: {
        allowPointSelect: true,
        cursor: "pointer",
        dataLabels: {
          enabled: true,
          format: "{point.percentage:.1f} %",
          distance: -20,
        },
        showInLegend: true,
        size: "120%",
        innerSize: "50%",
      },
    },
    legend: {
      align: "center",
      verticalAlign: "bottom",
      layout: "horizontal",
      y: 0,
      padding: 10,
    },
    series: [
      {
        name: "Delivered amount",
        data: customerIntentData,
      },
    ],
  };
  /**
   * aggregated customer intent bar chart config
   */
  const aggregatedCustomerIntentOptions = {
    chart: {
      type: "column",
      height: (3 / 19) * 100 + "%",
    },

    title: {
      text: "",
    },
    credits: {
      enabled: false,
    },
    xAxis: {
      gridLineWidth: 0,
      categories: displayDateCategory,
    },
    yAxis: {
      // gridLineWidth: 0,
      gridLineColor: "transparent",
    },
    yAxis: [
      {
        className: "highcharts-color-0",
        title: {
          text: "Positive Intent",
        },
      },
      {
        className: "highcharts-color-1",
        opposite: true,
        title: {
          text: "Negative Intent",
        },
      },
    ],

    plotOptions: {
      column: {
        borderRadius: 5,
      },
    },

    series: [
      {
        data: aggregatedCustomerIntentData[0],
        name: "Positive",
      },
      {
        data: aggregatedCustomerIntentData[1],
        name: "Negative",
        yAxis: 1,
      },
    ],
  };

  /**
   * change demo data based on changing day interval
   */

  // function chnageData() {
  //   // if (showRealData === false) {
  //   if (dateDifference === "7") {
  //     setCustomerIntentData([
  //       ["Positive", 15],
  //       ["Negative", 9],
  //     ]);

  //     if (localStorage.getItem("critical_factor_module") === "true") {
  //       setCallDistributionData([
  //         ["Switch", 8],
  //         ["Control", 3],
  //         ["Engine", 10],
  //         ["Assembly", 15],
  //       ]);
  //     } else {
  //       setCallDistributionData([
  //         ["Credit Card", 8],
  //         ["Saving Account", 3],
  //         ["Insurance", 10],
  //         ["Travel Card", 15],
  //       ]);
  //     }

  //     setAggregatedCustomerIntentData([
  //       [400, 415, 430, 390, 520, 450, 590],
  //       [300, 390, 500, 200, 200, 300, 400],
  //     ]);
  //     setManagerCommentData({
  //       count: 438,
  //       change: 3,
  //     });
  //     setOpportunityData({
  //       count: 200,
  //       change: 6,
  //     });
  //     setServiceRequestData({
  //       count: 178,
  //       change: -10,
  //     });
  //   }

  //   if (dateDifference === "30") {
  //     setCustomerIntentData([
  //       ["Positive", 300],
  //       ["Negative", 250],
  //     ]);

  //     if (localStorage.getItem("critical_factor_module") === "true") {
  //       setCallDistributionData([
  //         ["Switch", 80],
  //         ["Control", 180],
  //         ["Engine", 200],
  //         ["Assembly", 150],
  //       ]);
  //     } else {
  //       setCallDistributionData([
  //         ["Credit Card", 80],
  //         ["Saving Account", 180],
  //         ["Insurance", 200],
  //         ["Travel Card", 150],
  //       ]);
  //     }

  //     setAggregatedCustomerIntentData([
  //       [
  //         400, 415, 430, 390, 520, 450, 590, 400, 415, 430, 390, 520, 450, 590,
  //         400, 415, 430, 390, 520, 450, 590, 300, 390, 500, 200, 200, 300, 400,
  //         390, 520, 450, 590,
  //       ],
  //       [
  //         300, 390, 500, 200, 200, 300, 400, 390, 500, 200, 200, 390, 500, 200,
  //         200, 390, 500, 200, 200, 390, 390, 500, 200, 200, 390, 500, 200, 200,
  //         390, 500, 200, 200,
  //       ],
  //     ]);
  //     setManagerCommentData({
  //       count: 2190,
  //       change: -6,
  //     });
  //     setOpportunityData({
  //       count: 926,
  //       change: 10,
  //     });
  //     setServiceRequestData({
  //       count: 925,
  //       change: -15,
  //     });
  //   }

  //   if (dateDifference === "90") {
  //     setCustomerIntentData([
  //       ["Positive", 800],
  //       ["Negative", 999],
  //     ]);

  //     if (localStorage.getItem("critical_factor_module") === "true") {
  //       setCallDistributionData([
  //         ["Switch", 380],
  //         ["Control", 580],
  //         ["Engine", 600],
  //         ["Assembly", 350],
  //       ]);
  //     } else {
  //       setCallDistributionData([
  //         ["Credit Card", 380],
  //         ["Saving Account", 580],
  //         ["Insurance", 600],
  //         ["Travel Card", 350],
  //       ]);
  //     }

  //     setAggregatedCustomerIntentData([
  //       [
  //         400, 415, 430, 390, 520, 450, 590, 400, 590, 400, 415, 430, 390, 520,
  //         450, 590, 300, 390, 500, 200, 200, 300, 400, 390, 520, 450, 590, 400,
  //         415, 430, 390, 520, 450, 590, 400, 415, 430, 390, 520, 450, 590, 400,
  //         415, 430, 390, 520, 450, 590, 300, 390, 500, 200, 200, 300, 400, 390,
  //         520, 450, 590, 400, 415, 430, 390, 520, 450, 590, 400, 415, 430, 390,
  //         520, 450, 590, 400, 415, 430, 390, 520, 450, 590, 300, 390, 500, 200,
  //         200, 300, 400, 390, 520, 450, 590,
  //       ],
  //       [
  //         300, 390, 500, 200, 200, 300, 400, 390, 200, 200, 390, 500, 200, 200,
  //         390, 390, 500, 200, 200, 390, 500, 200, 200, 390, 500, 200, 200, 400,
  //         415, 430, 390, 520, 450, 590, 400, 415, 430, 390, 520, 450, 590, 400,
  //         415, 430, 390, 520, 450, 590, 300, 390, 500, 200, 200, 300, 400, 390,
  //         520, 450, 590, 200, 200, 400, 415, 430, 390, 520, 450, 590, 400, 415,
  //         430, 390, 520, 450, 590, 400, 415, 430, 390, 520, 450, 590, 200, 520,
  //         450, 590, 300, 390, 520, 450, 590,
  //       ],
  //     ]);
  //     setManagerCommentData({
  //       count: 6352,
  //       change: -17,
  //     });
  //     setOpportunityData({
  //       count: 2672,
  //       change: 18,
  //     });
  //     setServiceRequestData({
  //       count: 2588,
  //       change: 10,
  //     });
  //   }
  //   // }
  // }

  const handleFilterClick = (filter) => {
    const today = new Date();
    if (filter === "clear") {
      setActiveFilter(null);
      setDateDifference("");
      // reset to default 7-day view
      const from = new Date(today);
      from.setDate(today.getDate() - 7);
      fetchCollectionIntent(formatDMY(from), formatDMY(today));
    } else {
      setActiveFilter(filter);
      setDateDifference(filter);
      const from = new Date(today);
      from.setDate(today.getDate() - parseInt(filter));
      fetchCollectionIntent(formatDMY(from), formatDMY(today));
    }
  };

  // Highcharts config for non-agent roles (product-wise)
  const hierChartOptions = {
    chart: { type: "column", height: "300px" },
    title: { text: "" },
    credits: { enabled: false },
    xAxis: { categories: hierCategories, gridLineWidth: 0 },
    yAxis: {
      min: 0,
      gridLineColor: "transparent",
      title: { text: "Number of Calls" },
    },
    plotOptions: {
      column: {
        stacking: "normal",
        borderRadius: 3,
        groupPadding: 0.05,
        pointPadding: 0,
        dataLabels: { enabled: false },
      },
    },
    legend: {
      enabled: true,
      align: "center",
      verticalAlign: "bottom",
      layout: "horizontal",
    },
    series: hierSeries,
  };

  return (
    <div className="dashboard-body">
      <Header />
      <NavBar />

      <div>
        <div className="container-fluid">
          <div className="my-call">
            <div
              className="side-panel-with-table clearfix"
              style={{ display: "flex" }}
            >
              <ReportNav />

              <div className="call-table" style={{ paddingTop: "0px" }}>
                {/* Active filter banner */}
                {!isAgentRole && (
                  <ReportFilterBanner
                    filters={reportFilters}
                    onClear={handleReportFilterReset}
                  />
                )}
                {isAgentRole ? (
                  <>
                    <div className="my-calls-column">
                      <div className="calls-top-pannel">
                        <div className="row">
                          <div className="col-lg-6">
                            <div className="pannel-heading clearfix">
                              <div className="pannel-heading-icon">
                                <i
                                  class="fa fa-tachometer"
                                  aria-hidden="true"
                                ></i>
                              </div>
                              <div className="pannel-heading-info">
                                {storage.getItem("usecase") ===
                                "Complaints Management" ? (
                                  <h3>Intent Trends </h3>
                                ) : (
                                  <h3>Product by Intent </h3>
                                )}
                                {storage.getItem("usecase") ===
                                "Complaints Management" ? (
                                  <p>
                                    Track the overall Complaints Intent Trends
                                    over time
                                  </p>
                                ) : (
                                  <p>
                                    Analyses the intent of the product in detail
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>

                          <div className="col-lg-6">
                            <div className="pannel-nav clearfix">
                              <ul className="clearfix">
                                <div
                                  className="btn-group btn-group-toggle"
                                  data-toggle="buttons"
                                >
                                  <label
                                    className={`btn btn-default ${
                                      activeFilter === "7" ? "active" : ""
                                    }`}
                                    id="7"
                                    onClick={() => handleFilterClick("7")}
                                  >
                                    <input
                                      type="radio"
                                      name="options"
                                      autoComplete="off"
                                      style={{ outline: "none" }}
                                    />{" "}
                                    7 Days
                                  </label>
                                  <label
                                    className={`btn btn-default ${
                                      activeFilter === "30" ? "active" : ""
                                    }`}
                                    id="30"
                                    onClick={() => handleFilterClick("30")}
                                  >
                                    <input
                                      type="radio"
                                      name="options"
                                      autoComplete="off"
                                      style={{ outline: "none" }}
                                    />{" "}
                                    30 Days{" "}
                                  </label>
                                  <label
                                    className={`btn btn-default ${
                                      activeFilter === "90" ? "active" : ""
                                    }`}
                                    id="90"
                                    onClick={() => handleFilterClick("90")}
                                  >
                                    <input
                                      type="radio"
                                      name="options"
                                      autoComplete="off"
                                      style={{ outline: "none" }}
                                    />{" "}
                                    90 Days
                                  </label>
                                </div>
                              </ul>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-col gap-4">
                      {/* bar chart sectionnnnn  */}
                      <div
                        className="col-lg-12 bg-white"
                        style={{ paddingTop: "10px", paddingBottom: "10px" }}
                      >
                        <HighchartsReact
                          highcharts={Highcharts}
                          options={productWiseIntentOptions}
                        />
                      </div>
                      {/* call list section */}
                      <div
                        className="col-lg-12 bg-white"
                        style={{ marginTop: "8px", paddingBottom: "16px" }}
                      >
                        {/* Filter bar */}
                        <div
                          className="flex justify-end mb-4"
                          style={{
                            padding: "12px 0 0 0",
                            gap: "8px",
                            flexWrap: "wrap",
                            alignItems: "center",
                          }}
                        >
                          {/* Search */}
                          <input
                            type="text"
                            placeholder="Search..."
                            value={callSearchText}
                            onChange={(e) => setCallSearchText(e.target.value)}
                            className="search-tag"
                            style={{
                              border: "none",
                              background: "#eef2f5",
                              borderRadius: "10px",
                              width: "200px",
                              outline: "none",
                              padding: "10px",
                            }}
                          />
                          {/* Date Range Picker */}
                          <div style={{ position: "relative" }}>
                            <button
                              onClick={() => setDateRangeOpen((o) => !o)}
                              style={{
                                border: "none",
                                background: "#eef2f5",
                                borderRadius: "10px",
                                outline: "none",
                                padding: "10px 14px",
                                fontSize: "13px",
                                cursor: "pointer",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {callSelectionRange
                                ? `${callSelectionRange.startDate.toLocaleDateString()} – ${callSelectionRange.endDate.toLocaleDateString()}`
                                : "Select Date Range"}
                            </button>
                            {dateRangeOpen && (
                              <div
                                style={{
                                  position: "absolute",
                                  top: "44px",
                                  right: 0,
                                  zIndex: 999,
                                  boxShadow: "0 4px 16px rgba(0,0,0,0.15)",
                                  borderRadius: "8px",
                                  overflow: "hidden",
                                }}
                              >
                                <DateRangePicker
                                  ranges={[
                                    callSelectionRange || {
                                      startDate: new Date(),
                                      endDate: new Date(),
                                      key: "selection",
                                    },
                                  ]}
                                  onChange={handleCallDateSelect}
                                />
                                <div
                                  style={{
                                    display: "flex",
                                    justifyContent: "flex-end",
                                    gap: "8px",
                                    padding: "8px 12px",
                                    background: "#fff",
                                  }}
                                >
                                  <button
                                    onClick={() => {
                                      setCallSelectionRange(null);
                                      setDateRangeOpen(false);
                                    }}
                                    style={{
                                      border: "none",
                                      background: "#eef2f5",
                                      borderRadius: "6px",
                                      padding: "6px 12px",
                                      cursor: "pointer",
                                      fontSize: "12px",
                                    }}
                                  >
                                    Clear
                                  </button>
                                  <button
                                    onClick={() => setDateRangeOpen(false)}
                                    style={{
                                      border: "none",
                                      background: "#270086",
                                      color: "#fff",
                                      borderRadius: "6px",
                                      padding: "6px 12px",
                                      cursor: "pointer",
                                      fontSize: "12px",
                                    }}
                                  >
                                    Apply
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                          {/* Product */}
                          <select
                            value={callProductFilter}
                            onChange={(e) =>
                              setCallProductFilter(e.target.value)
                            }
                            style={{
                              border: "none",
                              background: "#eef2f5",
                              borderRadius: "10px",
                              outline: "none",
                              padding: "10px",
                              fontSize: "13px",
                            }}
                          >
                            <option value="">All Products</option>
                            {callsProducts.map((p) => (
                              <option key={p} value={p}>
                                {p}
                              </option>
                            ))}
                          </select>
                          {/* Outcome */}
                          <select
                            value={callOutcomeFilter}
                            onChange={(e) =>
                              setCallOutcomeFilter(e.target.value)
                            }
                            style={{
                              border: "none",
                              background: "#eef2f5",
                              borderRadius: "10px",
                              outline: "none",
                              padding: "10px",
                              fontSize: "13px",
                            }}
                          >
                            <option value="">All Outcomes</option>
                            {callsOutcomes.map((outcome) => (
                              <option key={outcome.value} value={outcome.value}>
                                {outcome.value}
                              </option>
                            ))}
                          </select>
                          {/* Clear */}
                          <button
                            onClick={() => {
                              setCallSearchText("");
                              setCallProductFilter("");
                              setCallOutcomeFilter("");
                              setCallSelectionRange(null);
                            }}
                            style={{
                              border: "none",
                              background: "#eef2f5",
                              borderRadius: "10px",
                              outline: "none",
                              padding: "10px",
                              fontSize: "13px",
                              cursor: "pointer",
                            }}
                          >
                            Clear
                          </button>
                        </div>

                        {/* Table */}
                        <div className="overflow-y-scroll">
                          <table className="ss">
                            <tbody>
                              <tr id="black-line">
                                {[
                                  "Call Date",
                                  "Customer",
                                  "Agent",
                                  "Language",
                                  "Product",
                                  "Outcome",
                                  "Action",
                                ].map((h) => (
                                  <th
                                    key={h}
                                    className="left-0 bg-white border-b-2 border-gray-200 px-5 py-4 text-left text-xl font-semibold tracking-wider text-black"
                                  >
                                    {h}
                                  </th>
                                ))}
                              </tr>

                              {callsData.length === 0 ? (
                                <tr>
                                  <td
                                    colSpan="7"
                                    style={{
                                      textAlign: "center",
                                      padding: "20px",
                                    }}
                                  >
                                    No Record Found
                                  </td>
                                </tr>
                              ) : (
                                callsData.map((call, i) => (
                                  <tr key={i}>
                                    <td className="border-b border-gray-200 px-5 py-2 w-40 text-xs">
                                      <p className="whitespace-no-wrap text-left text-gray-900">
                                        {call.call_date}
                                      </p>
                                    </td>
                                    <td className="border-b border-gray-200 px-5 py-2 w-40 text-xs whitespace-nowrap">
                                      <p className="whitespace-no-wrap text-left text-gray-900 whitespace-nowrap">
                                        {call.customer_name}
                                      </p>
                                    </td>
                                    <td className="border-b border-gray-200 px-5 py-2 w-40 text-xs whitespace-nowrap">
                                      <p className="whitespace-no-wrap text-left text-gray-900">
                                        {call.agent_name}
                                      </p>
                                    </td>
                                    <td className="border-b  whitespace-nowrap border-gray-200 px-5 py-2 w-40 text-xs">
                                      <p className="whitespace-no-wrap text-left text-gray-900">
                                        {call.language}
                                      </p>
                                    </td>
                                    <td className="border-b border-gray-200 px-5 py-2 w-40 text-xs whitespace-nowrap">
                                      <p className="whitespace-no-wrap text-left text-gray-900">
                                        {call.product_name}
                                      </p>
                                    </td>
                                    <td className="border-b border-gray-200 px-5 py-2 w-40 text-xs whitespace-nowrap">
                                      <p className="whitespace-no-wrap text-left text-gray-900 whitespace-nowrap">
                                        {call.outcome_raw || "—"}
                                      </p>
                                    </td>
                                    <td className="border-b border-gray-200 px-5 py-2 w-40 text-xs cursor-pointer">
                                      <a
                                        className="text-left text-gray-900 whitespace-nowrap"
                                        onClick={() =>
                                          call.call_id &&
                                          history.push(
                                            `/call/${call.call_id}/call-insight`
                                          )
                                        }
                                      >
                                        View More
                                      </a>
                                    </td>
                                  </tr>
                                ))
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="my-calls-column">
                    <div className="calls-top-pannel">
                      <div className="row">
                        <div className="col-lg-6">
                          <div className="pannel-heading clearfix">
                            <div className="pannel-heading-info">
                              <h3>Product by Intent</h3>
                              <p>
                                Analyses the intent of the product in detail
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-col gap-4">
                      <div
                        className="col-lg-12 bg-white"
                        style={{ paddingTop: "10px", paddingBottom: "10px" }}
                      >
                        <HighchartsReact
                          highcharts={Highcharts}
                          options={hierChartOptions}
                        />
                      </div>
                      <div
                        className="col-lg-12 bg-white"
                        style={{ marginTop: "8px", paddingBottom: "16px" }}
                      >
                        <div className="overflow-y-scroll">
                          <table className="ss">
                            <tbody>
                              <tr id="black-line">
                                {[
                                  "Call Date",
                                  "Agent",
                                  "Customer",
                                  "Product",
                                  "Collection Status",
                                  "PTP",
                                  "Collected",
                                  "Target",
                                  "Action",
                                ].map((h) => (
                                  <th
                                    key={h}
                                    className="left-0 bg-white border-b-2 border-gray-200 px-5 py-4 text-left text-xl font-semibold tracking-wider text-black"
                                  >
                                    {h}
                                  </th>
                                ))}
                              </tr>
                              {hierCalls.length === 0 ? (
                                <tr>
                                  <td
                                    colSpan="8"
                                    style={{
                                      textAlign: "center",
                                      padding: "20px",
                                    }}
                                  >
                                    No Record Found
                                  </td>
                                </tr>
                              ) : (
                                hierCalls.map((call, i) => (
                                  <tr key={i}>
                                    <td className="border-b border-gray-200 px-5 py-2 w-40 text-xs whitespace-nowrap">
                                      <p className="text-left text-gray-900">
                                        {call.call_date}
                                      </p>
                                    </td>
                                    <td className="border-b border-gray-200 px-5 py-2 w-40 text-xs whitespace-nowrap">
                                      <p className="text-left text-gray-900">
                                        {call.agent_name}
                                      </p>
                                    </td>
                                    <td className="border-b border-gray-200 px-5 py-2 w-40 text-xs whitespace-nowrap">
                                      <p className="text-left text-gray-900">
                                        {call.customer_name}
                                      </p>
                                    </td>
                                    <td className="border-b border-gray-200 px-5 py-2 w-40 text-xs whitespace-nowrap">
                                      <p className="text-left text-gray-900 whitespace-nowrap">
                                        {call.product_name}
                                      </p>
                                    </td>
                                    <td className="border-b border-gray-200 px-5 py-2 w-40 text-xs whitespace-nowrap">
                                      <p className="text-left text-gray-900 whitespace-nowrap">
                                        {call.collection_status}
                                      </p>
                                    </td>
                                    <td className="border-b border-gray-200 px-5 py-2 w-40 text-xs whitespace-nowrap">
                                      <p className="text-left text-gray-900">
                                        {call.ptp_status ? "Yes" : "No"}
                                      </p>
                                    </td>
                                    <td className="border-b border-gray-200 px-5 py-2 w-40 text-xs whitespace-nowrap">
                                      <p className="text-left text-gray-900">
                                        {call.collected_amount_formatted ??
                                          call.collected_amount}
                                      </p>
                                    </td>
                                    <td className="border-b border-gray-200 px-5 py-2 w-40 text-xs">
                                      <p className="text-left text-gray-900">
                                        {call.target_amount_formatted ??
                                          call.target_amount ??
                                          "-"}
                                      </p>
                                    </td>
                                    <td className="border-b border-gray-200 px-5 py-2 w-40 text-xs cursor-pointer">
                                      <a
                                        className="text-left text-gray-900 whitespace-nowrap"
                                        onClick={() =>
                                          call.call_id &&
                                          history.push(
                                            `/call/${call.call_id}/call-insight`
                                          )
                                        }
                                      >
                                        View More
                                      </a>
                                    </td>
                                  </tr>
                                ))
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />

      {/* Floating ReportFilter — non-agent roles only */}
      {!isAgentRole && (
        <>
          <button
            onClick={changeReportFilterWidth}
            title="Global Filters"
            style={{
              position: "fixed",
              transition: "0.5s",
              top: "30%",
              right: reportFilterWidth > 0 ? reportFilterWidth + 8 : 25,
              backgroundColor:
                Object.keys(reportFilters).length > 0 ? "#271078" : "#F99E52",
              color: "white",
              padding: "10px",
              height: "40px",
              width: "40px",
              zIndex: 2001,
              borderRadius: "10%",
              border: "none",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 4px 12px rgba(0,0,0,0.25)",
            }}
          >
            <FaFilter size={18} />
            {Object.keys(reportFilters).length > 0 && (
              <span
                style={{
                  position: "absolute",
                  top: "-4px",
                  right: "-4px",
                  backgroundColor: "#F99E52",
                  color: "#fff",
                  borderRadius: "50%",
                  width: "18px",
                  height: "18px",
                  fontSize: "10px",
                  fontWeight: "bold",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {Object.keys(reportFilters).length}
              </span>
            )}
          </button>

          <ReportFilter
            width={reportFilterWidth}
            changeSideBarWidth={changeReportFilterWidth}
            onApplyFilter={handleReportFilterApply}
            onResetFilter={handleReportFilterReset}
            userRole={role}
          />

          {isReportFilterOpen && (
            <div
              onClick={changeReportFilterWidth}
              style={{
                position: "fixed",
                inset: 0,
                backgroundColor: "rgba(0,0,0,0.35)",
                zIndex: 1999,
              }}
            />
          )}
        </>
      )}
    </div>
  );
};

export default ProductIntentReport;
